import { useQuery } from "@tanstack/react-query";
import {
  format,
  getHours,
  getMinutes,
  getSeconds,
  isAfter,
  isBefore,
  parseISO,
  set,
} from "date-fns";

import { createAxiosInstance } from "api/axiosConfig";
import { findPracticeHoursRange } from "components/AppointmentsList/utils/find-practive-hours-range";
import { generateTimeSlots } from "components/AppointmentsList/utils/generate-time-slots";
import { withMockedScheduleOverlaps } from "components/AppointmentsList/utils/mock-schedule-overlaps";
import {
  toDatePart,
  toScheduleDateTime,
} from "components/AppointmentsList/utils/schedule-date-time";

const isDoctorInRoster = (doctor) => {
  const rosterValue =
    doctor?.roster ??
    doctor?.permissions?.roster ??
    doctor?.staffMember?.roster;

  // Keep doctor visible when roster info is not present in payload.
  if (rosterValue === undefined || rosterValue === null) return true;

  return Boolean(rosterValue);
};

export const useAppointments = ({
  is5minsAppointment,
  hideDaysOff = false,
  selectedStartDate,
  selectedEndDate,
  selectedDoctors,
  filterOutOfRosterDoctors = false,
  enabled = true,
}) => {

  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: [
      "appointments",
      selectedStartDate,
      selectedEndDate,
      selectedDoctors,
      tenantVal,
      is5minsAppointment,
      hideDaysOff,
    ],
    queryFn: async () => {
      const axiosInstance = await createAxiosInstance();

      let staffMembersParam = "";

      if (selectedDoctors.length > 0) {
        staffMembersParam = `&staffMember=${selectedDoctors.join(",")}`;
      }

      const hideDaysOffParam = hideDaysOff ? "&hideDaysOff=true" : "";

      const response = await axiosInstance.get(
        `${tenantVal}/schedule?startDate=${format(
          selectedStartDate,
          "yyyy-MM-dd"
        )}&endDate=${format(
          selectedEndDate,
          "yyyy-MM-dd"
        )}${staffMembersParam}${hideDaysOffParam}`
      );

      const data = {
        result: response.data.result.map((day) => ({
          ...day,
          doctors: withMockedScheduleOverlaps(day.staffMembers)
            .map((doctor) => ({
              ...doctor,
              schedule: doctor.schedule
                .filter((meeting) => {
                  return meeting.appointmentStatus !== "CANCELLED";
                })
                .map((meeting) => ({
                  start: toScheduleDateTime({
                    dateTime: meeting.startTime,
                    time: meeting.startAt,
                  }),
                  end: toScheduleDateTime({
                    dateTime: meeting.endTime,
                    time: meeting.endAt,
                  }),
                  type:
                    meeting.appointmentType === "exception"
                      ? meeting.recurrenceType !== null
                        ? "recurring-exception"
                        : "standard-exception"
                      : meeting.appointmentType === "recurring"
                      ? "consultation"
                      : meeting?.appointmentType,
                  consultation: {
                    ...meeting,
                    id: meeting?.appointmentId,
                  },
                  exception: {
                    ...meeting,
                    startDate:
                      toDatePart(meeting?.exceptionStartDate) ||
                      toDatePart(meeting?.startTime),
                    endDate:
                      toDatePart(meeting?.exceptionEndDate) ||
                      toDatePart(meeting?.endTime),
                    reason: meeting?.exceptionType,
                    description: meeting?.exceptionReason,
                    startTime: meeting?.startAt
                      ?.map((time) => (time >= 10 ? `${time}` : `0${time}`))
                      .join(":"),
                    endTime: meeting?.endAt
                      ?.map((time) => (time >= 10 ? `${time}` : `0${time}`))
                      .join(":"),
                    isAllDay: meeting?.allDay,
                  },
                })),
              practiceHours: {
                start: `${day.date}T${doctor.practiceHours.startTime}`,
                end: `${day.date}T${doctor.practiceHours.endTime}`,
              },
            }))
            .filter(
              (doctor) =>
                (!selectedDoctors ||
                  selectedDoctors.length === 0 ||
                  selectedDoctors.includes(doctor.staffMemberId.toString())) &&
                (!filterOutOfRosterDoctors || isDoctorInRoster(doctor))
            ),
        })),
      };

      const practiveHoursRange = findPracticeHoursRange(data);

      const allAppointments = data.result.flatMap((day) =>
        day.doctors.flatMap((doctor) => doctor.schedule)
      );

      if (!practiveHoursRange && allAppointments.length === 0) {
        return {
          data: data.result,
          practiceHours: null,
          timeSlots: [],
        };
      }

      const baseDate = selectedStartDate;
      let extendedRange = practiveHoursRange
        ? { ...practiveHoursRange }
        : {
            earliestStart: set(baseDate, {
              hours: 23,
              minutes: 59,
              seconds: 59,
              milliseconds: 999,
            }),
            latestEnd: set(baseDate, {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            }),
          };

      allAppointments.forEach((appointment) => {
        if (appointment.start && appointment.end) {
          const appointmentStart = parseISO(appointment.start);
          const appointmentEnd = parseISO(appointment.end);

          // Normalize to the same calendar day as the practice hours range so
          // that the global timeSlots never spans more than one day.
          const normalizedStart = set(extendedRange.earliestStart, {
            hours: getHours(appointmentStart),
            minutes: getMinutes(appointmentStart),
            seconds: getSeconds(appointmentStart),
          });
          const normalizedEnd = set(extendedRange.latestEnd, {
            hours: getHours(appointmentEnd),
            minutes: getMinutes(appointmentEnd),
            seconds: getSeconds(appointmentEnd),
          });

          if (isBefore(normalizedStart, extendedRange.earliestStart)) {
            extendedRange.earliestStart = normalizedStart;
          }
          if (isAfter(normalizedEnd, extendedRange.latestEnd)) {
            extendedRange.latestEnd = normalizedEnd;
          }
        }
      });

      if (isAfter(extendedRange.earliestStart, extendedRange.latestEnd)) {
        return {
          data: data.result,
          practiceHours: null,
          timeSlots: [],
        };
      }

      const timeSlots = generateTimeSlots(extendedRange, is5minsAppointment);

      return {
        data: data.result,
        practiceHours: extendedRange,
        timeSlots,
      };
    },
    enabled: !!tenantVal && enabled,
    meta: { errorMessage: "Error fetching appointments" },
  });
};
